import { useTranslation } from 'react-i18next'
import { Link, useLocation } from 'react-router'
import { useAuth } from '../auth.jsx'
import LanguageSwitch from './LanguageSwitch.jsx'
import ThemeSwitch from './ThemeSwitch.jsx'
import UserMenu from './UserMenu.jsx'
import Logo from './Logo.jsx'

// A single feeder page (/feeders/:slug) is the "Watch live" screen.
const isLivePath = (p) => p === '/live' || /^\/feeders\/[^/]+/.test(p)

const LINKS = [
  { to: '/live', key: 'nav.watchLive', match: isLivePath },
  { to: '/feeders', key: 'nav.feeders', match: (p) => p === '/feeders' },
  { to: '/cats', key: 'nav.cats', match: (p) => p.startsWith('/cats') },
  { to: '/about', key: 'nav.about', match: (p) => p.startsWith('/about') },
]

/** `variant="hero"`: sits inside the mint hero block on the home page. */
export default function Header({ variant = 'default' }) {
  const { t } = useTranslation()
  const { user, isAdmin } = useAuth()
  const { pathname } = useLocation()

  const inner = (
    <div className="mx-auto flex max-w-7xl flex-wrap items-center justify-between gap-x-6 gap-y-3 px-4 py-4 sm:px-8 sm:py-5">
      <Logo />
      <nav className="order-last flex w-full flex-wrap gap-x-7 gap-y-2 text-base font-bold lg:order-none lg:w-auto">
        {LINKS.map((l) => {
          const active = l.match(pathname)
          return (
            <Link
              key={l.to}
              to={l.to}
              aria-current={active ? 'page' : undefined}
              className={`no-underline transition-colors hover:text-teal ${active ? 'text-teal' : 'text-ink'}`}
            >
              {t(l.key)}
            </Link>
          )
        })}
        {isAdmin && (
          <Link
            to="/admin"
            className={`no-underline transition-colors hover:text-teal ${pathname.startsWith('/admin') ? 'text-teal' : 'text-faint'}`}
          >
            {t('nav.admin')}
          </Link>
        )}
      </nav>
      <div className="flex items-center gap-3">
        <ThemeSwitch />
        <LanguageSwitch className="hidden sm:flex" />
        {user ? (
          <UserMenu />
        ) : (
          <Link
            to="/login"
            className="rounded-full bg-ink px-[22px] py-3 font-extrabold text-on-ink no-underline transition hover:opacity-90"
          >
            {t('nav.login')}
          </Link>
        )}
      </div>
      <LanguageSwitch className="sm:hidden" />
    </div>
  )

  return variant === 'hero' ? <header>{inner}</header> : <header className="border-b border-line">{inner}</header>
}
